import { Location } from '@atom/hooks/useCurrentLocation';

import { AssetRouteDataType, AttributeDataType } from './dataType';
import { LocationGeometry } from './geojson';
import { PolicyAction, PolicyGrant } from './policy';
import { ConversionUnit, SchemaBudgetType } from './schema';

export enum AssetDetailView {
  ASSET_INFO = 'assetInfo',
  SUB_ITEMS = 'subItems',
  WORK = 'work',
  FILES = 'files',
  PHOTOS = 'photos',
  COST = 'cost',
  REQUESTS = 'requests',
}

export enum CreationType {
  upload = 'upload',
  import = 'import',
  inventory = 'inventory',
  work = 'work',
}

// Rest routes use elements-changed which is invalid enum
// GQL routes resolve it and map to a proper enum
export enum ChangeType {
  ADDED = 'added',
  DELETED = 'deleted',
  EDITED = 'edited',
  ELEMENTS_CHANGED = 'elementsChanged',
  ELEMENTS_CHANGED_LEGACY = 'elements-changed',
  PARENT_ADDED = 'parentAdded',
}

export enum LocationType {
  Point = 'Point',
  LineString = 'LineString',
}

export type AssetUser = {
  firstName: string;
  lastName: string;
};

export type AssetAncestor = {
  id: string;
  name: string;
};

export type AssetCategory = {
  id: string;
  name: string;
};

export type LastWorkOrder = {
  id: string;
  name: string;
  updatedDate?: number;
};

export type AssetCursor = {
  total: number;
  index: number;
  next: string;
  previous: string;
};

export type AssetBudget = {
  isStockBased?: boolean;
  quantityOnHand?: number;
  type?: SchemaBudgetType;
  rate?: number;
  unit?: string;
  isStartEndReading?: boolean;
  isStartEndCalculated?: boolean;
  conversionUnits?: ConversionUnit[];
};

export type AssetAttribute = {
  id: string;
  name: string;
  dataType: AttributeDataType;
  value: any;
  newValue?: any;
  changeId?: string;
  defaultValue?: string;
  enumeration: string[];
  isEditable: boolean;
  isRequired?: boolean;
  unit: string;
  group?: string;
  order?: number;
  thousandsSeparator?: boolean;
  isVisibleAsSubtext?: boolean;
  isPending?: boolean;
  isPendingUpdate?: boolean;
  publishedAttribute?: AssetAttribute;
  attributeGroupId?: string;
};

export type AssetAttributeGroup = {
  id: string;
  name: string;
  expandByDefault?: boolean;
  attributes: AssetAttribute[];
};

export type AssetElementAttribute = {
  id: string;
  name: string;
  value?: any;
  unit?: string;
  dataType: AttributeDataType;
  isVisibleAsSubtext?: boolean;
};

export type AssetElement = {
  id: string;
  name: string;
  assetType: string;
  schemaId: string;
  markerId?: number;
  hasChildren: boolean;
  changeType?: ChangeType;
  hasDescendantChanges: boolean;
  includeChangesBefore?: number;
  attributes: AssetElementAttribute[];
};

export type AssetElementGroup = {
  id: string;
  name: string;
  elements: AssetElement[];
};

export type AssetDetail = {
  id: string;
  name: string;
  address?: string;
  externalId?: string;
  categoryId?: string;
  assetType: string;
  creationType: CreationType;
  schemaId: string;
  parentAssetId: string;
  colorId: number;
  markerId: number;
  isMaterial: boolean;
  mainPhotoFileId?: string;
  inactive?: boolean;
  archived?: boolean;
  ancestors: AssetAncestor[];
  changeType?: ChangeType;
  includeChangesBefore: number;
  lastAppliedWorkOrderName?: string;
  lastAppliedDate?: number;
  lastWorkOrder: LastWorkOrder;
  location?: LocationGeometry;
  locationType?: LocationType;
  attributeGroups: AssetAttributeGroup[];
  budget: AssetBudget;
  createdBy: AssetUser;
  createdDate: number;
  updatedBy: AssetUser;
  updatedDate: number;
  grants?: PolicyGrant[];
  actions?: PolicyAction[];
  categories?: AssetCategory[];
  cursor?: AssetCursor;
  elements?: AssetElement[];
  elementGroups?: AssetElementGroup[];
};

export type AssetConnectionItem = {
  id: string;
  name: string;
  assetType: string;
  schemaId?: string;
  attributes?: any;
  colorId: number;
  markerId: number;
  isMaterial: boolean;
  mainPhotoFileId?: string;
  archived?: boolean;
  createdBy: AssetUser;
  createdDate?: number;
  updatedBy?: AssetUser;
  updatedDate?: number;
  location?: LocationGeometry;
  schemaName?: string;
  budget?: AssetBudget;
  categories?: AssetCategory[];
};

export type AssetsConnection = {
  totalCount: number;
  assets: AssetConnectionItem[];
};

export type AssetsConnectionInput = {
  ids?: string[];
  omitIds?: string[];
  rootAssets?: boolean;
  schemaId?: string;
  categoryIds?: string[];
  hasPendingChangesOnly?: boolean;
  includeInactive?: boolean;
  budget?: { unit?: string };
  attributes?: { [key: string]: any };
  limit?: number;
  page?: number;
  sortBy?: string;
  isAscending?: boolean;
};

export type AssetTreeAttribute = {
  name: string;
  dataType: AttributeDataType;
  value?: any;
  unit?: string;
  defaultValue?: any;
  isVisibleAsSubtext?: boolean;
};

export type AssetTreeAttributeGroup = {
  id: string;
  name: string;
  expandByDefault: boolean;
  attributes: string[];
};

export type AssetTreeElementGroup = {
  id: string;
  name: string;
  elements?: AssetTree[];
};

export type AssetTree = {
  id: string;
  name: string;
  assetType: string;
  schemaId: string;
  markerId: number;
  attributes: { [attributeId: string]: AssetTreeAttribute };
  attributeGroups: AssetTreeAttributeGroup[];
  elements?: AssetTree[];
  elementGroups?: AssetTreeElementGroup[];
  hasAssets?: boolean;
  categories?: any;
  colorId?: string;
  archived?: boolean;
};

export type AssetsWithElementsConnection = {
  totalCount: number;
  assets: AssetTree[];
};

export type AssetsWithElementsInput = {
  schemaId: string;
  categoryIds?: string[];
  includeInactive?: boolean;
  isVisibleAsSubtextAttributesOnly?: boolean;
  limit?: number;
  page?: number;
  sortBy?: string;
};

export type SearchAssetsConnectionInput = {
  query: string;
  schemaId?: string;
  schemaIds?: string[];
  parentCategoryId?: string;
  includeMaterialAssets?: boolean;
  includeLocationBasedAssets?: boolean;
  includeInactive?: boolean;
  includeArchived?: boolean;
  page?: number;
  limit?: number;
  omitIds?: string[];
  budget?: {
    unit?: string;
  };
  attributes?: {
    id: string;
    value: any;
  }[];
};

export type SearchAssetsWithElementsConnectionInput = {
  query: string;
  schemaId?: string;
  schemaIds?: string[];
  parentCategoryId?: string;
  includeMaterialAssets?: boolean;
  includeLocationBasedAssets?: boolean;
  includeInactive?: boolean;
  includeArchived?: boolean;
  page?: number;
  limit?: number;
  omitIds?: string[];
  budget?: {
    unit?: string;
  };
  attributes?: {
    id: string;
    value: any;
  }[];
  includeAttributesProperty?: boolean;
  isVisibleAsSubtextAttributesOnly?: boolean;
  includeElements?: boolean;
};

export type SearchAssetBudget = {
  isStockBased?: boolean;
  quantityOnHand?: number;
  rate?: number;
  unit?: string;
};

export type SearchAsset = {
  id: string;
  schemaId: string;
  externalId: string;
  name: string;
  assetType: string;
  markerId: number;
  colorId?: number;
  categoryPath: string[];
  isMaterial?: boolean;
  locationType?: LocationType | null;
  inactive?: boolean;
  archived?: boolean;
  budget?: SearchAssetBudget;
};

export type SearchAssetsConnection = {
  totalCount: number;
  assets: SearchAsset[];
};

export type SearchAssetWithElements = {
  id: string;
  schemaId: string;
  externalId: string;
  name: string;
  assetType: string;
  markerId: number;
  colorId?: number;
  categoryPath: string[];
  isMaterial?: boolean;
  locationType?: string;
  inactive?: boolean;
  archived?: boolean;
  budget?: SearchAssetBudget;
  isSubItem?: boolean;
  ancestors?: {
    id: string;
    name: string;
  }[];
  categories?: {
    id: string;
    name: string;
  }[];
  attributes?: { [attributeId: string]: AssetTreeAttribute };
  attributeGroups?: AssetTreeAttributeGroup[];
};

export type SearchAssetsWithElementsConnection = {
  totalCount: number;
  assets: SearchAssetWithElements[];
};

export type AssetCreateBudgetInput = {
  rate: number;
  unit: string;
  quantityOnHand?: number;
  quantityRemaining?: number;
  isStockBased: boolean;
  isStartEndReading: boolean;
  isStartEndCalculated: boolean;
};

export type AssetCreateAttributesInput = {
  [map: string]: {
    value: any;
  };
};

export type AssetCreateInput = {
  name?: string;
  externalId?: string;
  schemaId: string;
  categoryId?: string;
  attributes?: AssetCreateAttributesInput;
  location?: Location;
  budget?: Partial<AssetCreateBudgetInput>;
};

export type AssetUpdateBudgetInput = {
  rate?: number;
  unit?: string;
  isStockBased?: boolean;
  isStartEndReading?: boolean;
  isStartEndCalculated?: boolean;
  quantityOnHand?: number;
  quantityRemaining?: number;
};

export type AssetUpdateInput = {
  id: string;
  name?: string;
  externalId?: string;
  attributes?: AssetCreateAttributesInput;
  location?: Location;
  budget?: AssetUpdateBudgetInput;
  mainPhotoFileId?: string;
};

export type AssetAttributesUpdateInput = {
  assetId: string;
  attributes: {
    [id: string]: {
      value?: any;
      newValue?: any;
    };
  };
};

export type AssetDeleteInput = {
  id: string;
};

export type AssetChangesApprovalInput = {
  id: string;
  includeChangesBefore: number;
  rootAssetId: string;
};

export type AssetAttributeChangeInput = {
  assetId: string;
  attributeId: string;
  changeId: string;
  rootAssetId: string;
};

export type AssetElementQuantityInput = {
  schemaId: string;
  quantity: number;
  elements?: AssetElementQuantityInput[];
};

export type AssetElementsCreateInput = {
  assetId: string;
  assetSchemaId: string;
  elements?: AssetElementQuantityInput[];
};

export type MoveElementInput = {
  elementId: string;
  destinationParentAssetId?: string;
  validateOnly?: boolean;
};

export type ReplaceElementInput = {
  elementId: string;
  destinationParentAssetId: string;
  replacementElementId: string;
};

export type ElementErrorTask = {
  workOrderId: string;
  workOrderName: string;
  taskId?: string;
  taskName?: string;
};

export type ElementError = {
  id: string;
  name: string;
  hasOtherAssets?: ElementErrorTask[];
  hasFormAssets?: ElementErrorTask[];
};

export type AssetRouteDataItem = {
  title?: string;
  dataType?: AssetRouteDataType;
  enumeration?: string[];
  value?: any;
};

export type AssetRouteAutocomplete = {
  components?: AssetRouteDataItem[];
  milePostRanges?: number[][];
};

export type AssetRouteSimpleInputItem = {
  title: string;
  value: string;
};

export type AssetRouteSimpleInput = {
  geometry: Partial<Location>;
  components?: AssetRouteSimpleInputItem[];
};

export type AssetUsageConnectionInput = {
  assetId: string;
  page?: number;
  limit?: number;
};

export type AssetUsage = {
  id: string;
  name: string;
  statusId?: string;
  dueDate?: number;
  completionDate?: number;
  totalUsage?: number;
  totalCost?: number;
};

export type AssetUsageConnection = {
  totalCount: number;
  usage: AssetUsage[];
};

export type AssetUsageSummary = {
  totalUsage?: number;
  totalUsageToday?: number;
  totalRemaining?: number;
  quantityOnHand?: number;
  totalCost?: number;
  totalCostYear?: number;
  totalCostMonth?: number;
  rate?: number;
};

export type AssetChangeTypesInput = {
  rootAssetId: string;
  assetIds: string[];
};

export type AssetNodeChangeState = {
  assetId: string;
  changeType: ChangeType | null;
  hasDescendantChanges: boolean;
};
